import { useCallback, useEffect, useState } from 'react';
import { companyApi, type Company } from '../lib/api/company';
import { formatVND } from '../lib/format';
import { useToast } from '../lib/use-toast';
import { TableActionMenu } from './TableActionMenu';
import { employeesApi, type EmployeePickerItem } from '../lib/api/employees';
import { SearchableDropdown } from './SearchableDropdown';

export function CompanyInfoSection() {
  const { toast, showToast } = useToast();
  const [c, setC] = useState<Company | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [bankMenuId, setBankMenuId] = useState<string | null>(null);
  const [employees, setEmployees] = useState<EmployeePickerItem[]>([]);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const data = await companyApi.get();
      setC(data);
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { employeesApi.picker().then(setEmployees).catch(() => setEmployees([])); }, []);

  const syncOfficer = async (field: 'legal' | 'ktt', employeeId: string) => {
    const employee = employees.find((item) => item.id === employeeId);
    if (!employee) return;
    try {
      await companyApi.patch(field === 'legal'
        ? { legalRepName: employee.name, legalRepRole: employee.position ?? 'Đại diện pháp luật' }
        : { kttName: employee.name });
      showToast(`Đã đồng bộ ${field === 'legal' ? 'đại diện pháp luật' : 'Kế toán trưởng'} từ HR`);
      load();
    } catch (error) { showToast(error instanceof Error ? error.message : String(error)); }
  };

  const editField = async (label: string, field: string, currentValue: string | null) => {
    const v = window.prompt(`Sửa ${label}?`, currentValue ?? '');
    if (v == null) return;
    try {
      await companyApi.patch({ [field]: v } as Partial<Company>);
      showToast(`Đã cập nhật ${label}`);
      load();
    } catch (e) { alert(e instanceof Error ? e.message : String(e)); }
  };

  const addBank = async () => {
    const bankName = window.prompt('Tên ngân hàng (Vietcombank / Techcombank / MB Bank / TPBank…)');
    if (!bankName?.trim()) return;
    const bankCode = window.prompt('Mã ngắn (VCB / TCB / MB / TPB…)', bankName.slice(0, 3).toUpperCase());
    if (!bankCode?.trim()) return;
    const account = window.prompt('Số TK?');
    if (!account?.trim()) return;
    try {
      await companyApi.addBank({
        bankName: bankName.trim(),
        bankCode: bankCode.trim().toUpperCase(),
        colorHex: '#2563EB',
        account: account.trim(),
        holder: c?.name ?? 'CÔNG TY',
        branch: null,
        isDefault: (c?.banks.length ?? 0) === 0,
      });
      showToast('Đã thêm TK ngân hàng');
      load();
    } catch (e) { alert(e instanceof Error ? e.message : String(e)); }
  };

  const setDefaultBank = async (id: string) => {
    try {
      await companyApi.patchBank(id, { isDefault: true });
      showToast('Đã đặt làm mặc định');
      load();
    } catch (e) { alert(e instanceof Error ? e.message : String(e)); }
  };

  const removeBank = async (id: string, name: string) => {
    if (!window.confirm(`Xoá TK ${name}?`)) return;
    try {
      await companyApi.removeBank(id);
      showToast('Đã xoá');
      load();
    } catch (e) { alert(e instanceof Error ? e.message : String(e)); }
  };

  if (loading) return <div className="pay-empty">Đang tải…</div>;
  if (err) return <div className="pay-table-error">Lỗi: {err}</div>;
  if (!c) return <div className="pay-empty">Chưa có thông tin công ty. Gọi API /company (PATCH) để khởi tạo.</div>;

  return (
    <>
      <div className="cmp-hero">
        <div className="cmp-hero-mono">{c.shortName?.slice(0, 2).toUpperCase() ?? 'HK'}</div>
        <div className="cmp-hero-meta">
          <h2>{c.name}</h2>
          <p>MST {c.taxCode} · Thành lập {c.foundedDate ? new Date(c.foundedDate).toLocaleDateString('vi-VN') : '—'} · Chế độ {c.accountingRegime}</p>
        </div>
        <button type="button" className="cmp-hero-btn" onClick={() => showToast('Wizard sửa full — click từng dòng bên dưới để chỉnh trực tiếp')}>Sửa thông tin</button>
      </div>

      <div className="cmp-grid-2">
        <div className="cmp-card">
          <div className="cmp-card-head">
            <div><div className="cmp-card-icon"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6" /></svg></div><h3>Thông tin pháp lý</h3></div>
          </div>
          <dl className="cmp-dl">
            <ClickRow label="Tên đầy đủ" value={c.name} onEdit={() => editField('Tên đầy đủ', 'name', c.name)} />
            <ClickRow label="Tên viết tắt" value={c.shortName ?? '—'} onEdit={() => editField('Tên viết tắt', 'shortName', c.shortName)} />
            <ClickRow label="Mã số thuế" value={c.taxCode} mono onEdit={() => editField('MST', 'taxCode', c.taxCode)} />
            <ClickRow label="Ngày cấp GPKD" value={c.foundedDate ? new Date(c.foundedDate).toLocaleDateString('vi-VN') : '—'} onEdit={() => editField('Ngày cấp (yyyy-mm-dd)', 'foundedDate', c.foundedDate)} />
            <ClickRow label="Nơi cấp" value={c.licenseIssuer ?? '—'} onEdit={() => editField('Nơi cấp', 'licenseIssuer', c.licenseIssuer)} />
            <ClickRow label="Loại hình DN" value={c.entityType ?? '—'} onEdit={() => editField('Loại hình DN', 'entityType', c.entityType)} />
            <ClickRow label="Vốn điều lệ" value={<b>{formatVND(c.charteredCapital)}</b>} onEdit={() => editField('Vốn điều lệ (VND)', 'charteredCapital', String(c.charteredCapital))} />
          </dl>
        </div>

        <div className="cmp-card">
          <div className="cmp-card-head">
            <div><div className="cmp-card-icon"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" /></svg></div><h3>Địa chỉ &amp; liên hệ</h3></div>
          </div>
          <dl className="cmp-dl">
            <ClickRow label="Trụ sở" value={c.address ?? '—'} onEdit={() => editField('Địa chỉ trụ sở', 'address', c.address)} />
            <ClickRow label="Điện thoại" value={c.phone ?? '—'} onEdit={() => editField('Điện thoại', 'phone', c.phone)} />
            <ClickRow label="Email" value={c.email ?? '—'} onEdit={() => editField('Email', 'email', c.email)} />
            <ClickRow label="Website" value={c.website ?? '—'} onEdit={() => editField('Website', 'website', c.website)} />
            <ClickRow label="Fax" value={c.fax ?? '—'} onEdit={() => editField('Fax', 'fax', c.fax)} />
          </dl>
          <div className="cmp-branches-head">Chi nhánh ({c.branches.length})</div>
          <div className="cmp-branches">
            {c.branches.map((b) => (
              <div key={b.id} className="cmp-branch">
                <span className="cmp-branch-dot" />
                <div>
                  <b>{b.name}</b>
                  <div className="cmp-branch-sub">{b.address}{b.phone && ` · ${b.phone}`}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="cmp-grid-2">
        <div className="cmp-card">
          <div className="cmp-card-head">
            <div><div className="cmp-card-icon"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" /></svg></div><h3>Đại diện pháp luật &amp; KTT</h3></div>
          </div>
          <div className="cmp-people">
            {c.legalRepName && (
              <div className="cmp-person">
                <div className="cmp-person-avatar orange">{c.legalRepName.split(' ').slice(-2).map((w) => w[0]).join('').toUpperCase()}</div>
                <div>
                  <b>{c.legalRepName}</b>
                  <div className="cmp-person-role">{c.legalRepRole ?? 'Đại diện pháp luật'}</div>
                  <div className="cmp-person-meta">CCCD {c.legalRepCccd ?? '—'}{c.legalRepDob && ` · Sinh ${new Date(c.legalRepDob).toLocaleDateString('vi-VN')}`}</div>
                </div>
              </div>
            )}
            {c.kttName && (
              <div className="cmp-person">
                <div className="cmp-person-avatar purple">{c.kttName.split(' ').slice(-2).map((w) => w[0]).join('').toUpperCase()}</div>
                <div>
                  <b>{c.kttName}</b>
                  <div className="cmp-person-role">Kế toán trưởng</div>
                  {c.kttCert && <div className="cmp-person-extra">Chứng chỉ {c.kttCert}</div>}
                  <div className="cmp-person-meta">CCCD {c.kttCccd ?? '—'}{c.kttDob && ` · Sinh ${new Date(c.kttDob).toLocaleDateString('vi-VN')}`}</div>
                </div>
              </div>
            )}
          </div>
          <div className="cmp-hr-officers">
            <div><small>Đại diện pháp luật · nguồn HR</small><SearchableDropdown value="" placeholder="Chọn nhân viên HR" ariaLabel="Chọn đại diện pháp luật" onChange={(id) => syncOfficer('legal', id)} options={employees.map((employee) => ({ value: employee.id, label: `${employee.code ? `${employee.code} · ` : ''}${employee.name}${employee.position ? ` · ${employee.position}` : ''}` }))} /></div>
            <div><small>Kế toán trưởng · nguồn HR</small><SearchableDropdown value="" placeholder="Chọn nhân viên HR" ariaLabel="Chọn kế toán trưởng" onChange={(id) => syncOfficer('ktt', id)} options={employees.map((employee) => ({ value: employee.id, label: `${employee.code ? `${employee.code} · ` : ''}${employee.name}${employee.position ? ` · ${employee.position}` : ''}` }))} /></div>
          </div>
        </div>

        <div className="cmp-card">
          <div className="cmp-card-head">
            <div><div className="cmp-card-icon"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2 3 7l9 5 9-5-9-5z" /><path d="m3 17 9 5 9-5M3 12l9 5 9-5" /></svg></div><h3>Chế độ kế toán &amp; Thuế</h3></div>
          </div>
          <dl className="cmp-dl">
            <div><dt>Chế độ kế toán</dt><dd><span className="coa-code-pill parent">{c.accountingRegime}</span></dd></div>
            <div><dt>Chuẩn mực</dt><dd>{c.vasCompliance}</dd></div>
            <div><dt>Đồng tiền hạch toán</dt><dd>{c.reportingCurrency}</dd></div>
            <div><dt>Kỳ khai thuế GTGT</dt><dd><span className="coa-code-pill parent">{c.vatPeriod}</span></dd></div>
            <div><dt>PP tính thuế GTGT</dt><dd>{c.vatMethod}</dd></div>
            <div><dt>Thuế suất TNDN</dt><dd><b>{(c.cit * 100).toFixed(0)}%</b></dd></div>
          </dl>
        </div>
      </div>

      <div className="cmp-card">
        <div className="cmp-card-head">
          <div><h3>Tài khoản ngân hàng công ty</h3><p className="sal-card-sub">{c.banks.length} TK</p></div>
          <button type="button" className="pay-btn-dark" onClick={addBank}>+ Thêm TK</button>
        </div>
        <table className="cmp-tbl">
          <thead>
            <tr>
              <th>Ngân hàng</th><th>Số TK</th><th>Chủ TK</th><th>Chi nhánh</th><th>Mặc định</th><th />
            </tr>
          </thead>
          <tbody>
            {c.banks.map((b) => (
              <tr key={b.id}>
                <td>
                  <div className="cmp-bank-cell">
                    <div className="cmp-bank-logo" style={{ background: b.colorHex }}>{b.bankCode}</div>
                    <b>{b.bankName}</b>
                  </div>
                </td>
                <td className="sal-mono">{b.account}</td>
                <td>{b.holder}</td>
                <td>{b.branch ?? '—'}</td>
                <td>
                  {b.isDefault ? (
                    <span className="cmp-default-pill">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
                      Mặc định
                    </span>
                  ) : (
                    <button type="button" className="pay-code-btn" onClick={() => setDefaultBank(b.id)}>Đặt mặc định</button>
                  )}
                </td>
                <td className="col-act">
                  {!b.isDefault && <TableActionMenu
                    open={bankMenuId === b.id}
                    onToggle={() => setBankMenuId((current) => current === b.id ? null : b.id)}
                    onClose={() => setBankMenuId(null)}
                    ariaLabel={`Thao tác tài khoản ${b.bankName}`}
                    items={[
                      { label: 'Đặt làm mặc định', onClick: () => setDefaultBank(b.id) },
                      { label: 'Xoá tài khoản', tone: 'danger', dividerBefore: true, onClick: () => removeBank(b.id, b.bankName) },
                    ]}
                  />
                  }
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {toast && <div className="coa-toast">{toast}</div>}
    </>
  );
}

function ClickRow({ label, value, mono, onEdit }: { label: string; value: React.ReactNode; mono?: boolean; onEdit: () => void }) {
  return (
    <div onClick={onEdit} style={{ cursor: 'pointer' }} title="Click để sửa">
      <dt>{label}</dt>
      <dd className={mono ? 'sal-mono' : undefined}>{value}</dd>
    </div>
  );
}
